// 初始化日志过滤器 - 必须在最开始，以过滤第三方库的日志
import './fix';
import { initLogFilter } from './utils/log-filter';
import { GtmPlugin } from '@eureka/shared/src/utils/gtm';
import { getAgentSourceType, getUrlParams } from '@eureka/shared/src/utils/url';
import { createApp } from 'vue';
import App from './App.vue';
import '@patsnap-biz/design-tokens/scss/_variables-RD.css';
import '@patsnap-biz/design-tokens/scss/_el-tooltip.scss';
// Element Plus
import ElementPlus from 'element-plus';
import 'element-plus/dist/index.css';

// App styles (Tailwind + base styles)
import './style.scss';

// Element Plus theme overrides (must be after Element Plus CSS)
import './element-plus-theme.scss';

// 手动引入patsnap-biz样式
import '@patsnap-biz/nav-user-avatar/lib/style';
import '@patsnap-biz/icon/lib/style';
import '@patsnap-biz/toast/lib/style';
import '@patsnap-biz/ip-nav/lib/style';

// 引入 KaTeX CSS（用于渲染数学公式）
import 'katex/dist/katex.min.css';
import { startup } from '@eureka/shared/src/utils/startup/index';
import { getHelpCenter } from '@eureka/shared/src/utils/startup/help-center';
import { headerService as headerServiceInstance } from './services/header';
import { resetHeaderServiceDeferred, clearAccountCache } from '@eureka/shared/src/utils/header-service-utils';
import { i18n } from './i18n';
import { initTransformCodeInterceptor, initLanguageInterceptor, injectFingerprintInterceptor } from '@eureka/shared/src/utils/http';
import { initFreeUriInterceptor } from './utils/free-uri-interceptor';
import router from './router';
import { AnonymousError, isAnonymousError } from '@eureka/shared/src/exception';
import { passportInterceptManager } from '@eureka/shared/src/utils/startup/enable-token-base';
import { prepareTokenDeferred } from '@eureka/shared/src/utils/account';
import { initLoginSdk } from './utils/login-sdk-utils';
import { track, TrackEventType, TrackSourceType } from '@eureka/shared/src/utils/track';
import { initSuperAdminStatus, initUserRoles } from './config/admin';
import { PaymentSuccessHandler } from './services/payment-success-handler';
import { initUserApi, getDraftingPlanApi } from '@/apis/user';
import { addDraftingTrailApi } from '@/features/home/api';
import { DraftingPlan } from '@/types/user';
import { executeMigration } from './composables/useMigrationGuard';
import { entryCookie, initCookie } from '@eureka/shared/src/utils/init-cookie';
import { getIsOverseas } from '@eureka/shared/src/utils/site-region';

initLogFilter();

// 添加全局路由守卫处理支付成功回调
router.beforeEach(async (to, from, next) => {
    // 检测支付成功回调
    if (PaymentSuccessHandler.isPaymentSuccessCallback(to)) {
        try {
            // 异步处理支付成功逻辑，不阻塞路由导航
            const paymentHandler = new PaymentSuccessHandler(router, i18n);
            // 使用 setTimeout 确保路由导航不被阻塞
            setTimeout(() => {
                paymentHandler.handlePaymentCallback(to).catch(error => {
                    console.error('Payment success handling failed:', error);
                });
            }, 100);
        } catch (error) {
            console.error('Payment success handler initialization failed:', error);
        }
    }

    next();
});

// 全局类型声明
declare global {
  interface Window {
    __reinitializeApp?: () => Promise<void>;
  }
}

// ContentSquare 数据分析探针 - 只在生产环境加载
if (import.meta.env.PROD) {
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://t.contentsquare.net/uxa/4b78541d5bf56.js';
    script.onerror = () => {
        console.error('Failed to load ContentSquare script');
    };
    document.head.appendChild(script);
}

console.log('env: ', import.meta.env);

// 初始化 HTTP 拦截器
initTransformCodeInterceptor(i18n.global, 'commonStatusCode');

// 初始化语言拦截器（自动添加 Accept-Language header）
initLanguageInterceptor();

// 注入浏览器指纹拦截器
injectFingerprintInterceptor();

// 游客模式：自动将 DRAFTING_URI 替换为 FREE_DRAFTING_URI
initFreeUriInterceptor();

// 配置允许未登录访问的页面（白名单）
const allowAnonymousPageRegExps = [
    /^#?\/(\?.*)?$/, // 首页
    /^#?\/templates$/, // 模板列表页面
    /^#?\/plg-ip-home$/, // IP 首页
    /^#?\/patent-drafting(\?.*)?$/, // 专利起草页面（支持查询参数）
    /^#?\/upgrade(\?.*)?$/, // 支付页面
];

// 判断当前页面是否允许未登录访问
const isAnonymousAllowed = () => {
    // Hash 路由模式下，使用 window.location.hash
    const currentPath = window.location.hash || '#/';
    return allowAnonymousPageRegExps.some(pathReg => pathReg.test(currentPath));
};

// Vue 应用实例（全局保存，只创建一次）
let app: ReturnType<typeof createApp> | null = null;

// 初始化登录 SDK（全局使用，非 Vue 生命周期内使用纯 TS 工具函数）
const { openLoginDialog, handleThirdPartyLoginErrors } = initLoginSdk();

handleThirdPartyLoginErrors();

// 数据初始化函数（可重复调用）
async function reinitializeData() {
    try {
        await entryCookie.init();
    } catch (error) {
        console.error('Failed to initialize entry cookie or track page view!:', error);
    }

    // 重新创建 headerService 的 deferred，避免返回旧的 promise
    resetHeaderServiceDeferred(headerServiceInstance);

    const [headerService, locale, pageData, setting] = await startup({
        headerService: headerServiceInstance,
        projectName: 'agentic',
        preset: {
            name: 'agentic',
            preRedirectLogin() {
                const isInWhitelist = isAnonymousAllowed();
                const isLoggedIn = headerServiceInstance?.userCenterService?.logged;
                const isOverseas = getIsOverseas();

                if (!isLoggedIn && !isOverseas) {
                    openLoginDialog(true);
                } else if (!isInWhitelist && !isLoggedIn) {
                    openLoginDialog();
                }

                // 如果从登录态过渡到未登录态（如登录过期），且当前页面允许匿名访问，弹出登录框
                if(isLoggedIn){
                    // 移除 passport 拦截器，允许匿名访问
                    // 设置 prepareTokenDeferred 为 null，表示未登录
                    passportInterceptManager.eject();
                    prepareTokenDeferred.resolve(null as any);
                    openLoginDialog()
                        .catch(() => {
                            // 如果用户取消登录或登录失败，保持在当前页面刷新进入游客模式。
                            window.location.reload();
                        });
                }

                // 始终抛出 AnonymousError 阻止重定向到外部登录页
                throw new AnonymousError();
            },
        },
        pageInit: async () => {
            let plan: null |DraftingPlan = null;
            try {
                plan = await getDraftingPlanApi();
            } catch (error) {
                console.warn('[pageInit] Failed to fetch drafting plan:', error);
            }
            try {
                await initUserApi();
            } catch (error) {
                console.warn('[pageInit] User initialization failed:', error);
            }

            return {
                initialized: true,
                timestamp: Date.now(),
                plan,
            };
        },
        disablePrepareToken: false,
        i18n: i18n.global,
    });

    getHelpCenter(i18n.global, headerServiceInstance);

    // 赋权（仅海外环境）
    if (getIsOverseas()) {
        try {
            const result = await addDraftingTrailApi();
            if (result.success) {
                const passport = await prepareTokenDeferred.promise;
                if (passport) {
                    localStorage.removeItem('patsnap_header_account_info');
                    passport.removeLocal();
                    window.location.reload();
                }
            }
        } catch (e) {
            console.error('[addDraftingTrail]', e);
        }
    }

    // 登录后执行游客数据迁移
    executeMigration().catch(error => {
        console.error('[reinitializeData] Migration failed:', error);
    });

    return { headerService, locale, pageData, setting };
}

// 应用初始化函数（只执行一次）
async function initApp() {
    try {
        await reinitializeData();

        // 初始化超级管理员权限状态和用户角色
        await initSuperAdminStatus();
        await initUserRoles();
        track({
            event_type: TrackEventType.PlgPageView,
            source_type: TrackSourceType.AgentHome,
            event_detail_str: JSON.stringify({
                signatureId: initCookie.getVisitorId(),
                agent_name: getAgentSourceType(),
                agentSource: getUrlParams().get('start_from') || 'agent_home',
            }),
        });

        // 只在第一次初始化时创建和挂载 Vue 应用
        if (!app) {
            app = createApp(App);
            app.use(ElementPlus);
            app.use(i18n);
            app.use(router);
            app.use(GtmPlugin, router);
            app.mount('#app');
        }
    } catch (error) {
        // 如果是 AnonymousError，表示用户未登录但可以继续访问
        if (isAnonymousError(error)) {
            if (!app) {
                app = createApp(App);
                app.use(ElementPlus);
                app.use(i18n);
                app.use(router);
                app.mount('#app');
            }
        } else {
            // 其他错误才真正报错

        }
    }
}

// 暴露重新初始化函数给全局使用（登录成功后调用）
window.__reinitializeApp = async () => {
    try {
        // 1. 清除账号缓存
        await clearAccountCache(import.meta.env.VITE_ACCOUNT_SERVICE_URI as string);

        // 2. 重新初始化数据（会重新创建 headerService 的 deferred）
        await reinitializeData();

        // 3. 重新初始化超级管理员权限状态和用户角色
        await initSuperAdminStatus();
        await initUserRoles();
    } catch (error) {
        if (isAnonymousError(error)) {
            // 匿名模式，继续运行
        } else {

            // 如果重新初始化失败，降级为刷新页面
            window.location.reload();
        }
    }
};

// 执行初始化
initApp();
